import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import ApiClient from '../utils/ApiClient';
import TrackingInput from '../components/Returns/TrackingInput';
import TrackingIdModalDataTable from '../components/Returns/TrackingIdModalDataTable';
import ReceiveUnitsModal from '../components/Returns/ReceiveUnitsModal';
import RecentTrackingList from '../components/Returns/RecentTrackingList';
import MerchantReturns from '../components/Returns/MerchantReturns';

function UserTable({ onDataReceived, onTrackingSelect, trackingInputRef }) {
    return (
        <div className="px-4 sm:px-6 lg:px-8">
            <TrackingInput ref={trackingInputRef} onDataReceived={onDataReceived} />
            <RecentTrackingList onTrackingSelect={onTrackingSelect} />
        </div>
    );
}

// Two different jobs share this page: receiving FBA removal shipments back into the
// warehouse, and tracking customer returns on merchant-fulfilled orders.
const TABS = [
    { key: 'fba', label: 'FBA removals' },
    { key: 'mfn', label: 'Merchant returns' },
];

function Returns() {
    const [activeTab, setActiveTab] = useState('fba');
    const [trackingData, setTrackingData] = useState(null);
    const [trackingId, setTrackingId] = useState(null);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const [dispositionData, setDispositionData] = useState(null);
    const [loading, setLoading] = useState(false); // Loading state for API calls
    const [generatedCases, setGeneratedCases] = useState([]); // Store all generated cases with QR codes
    const trackingInputRef = useRef(null);

    const handleDataReceived = (data, trackingIdValue) => {
        // Clear previous session data when new tracking number scanned
        handleNewScan();
        setTrackingData(data);
        setTrackingId(trackingIdValue);
    };

    const handleTrackingSelect = (trackingNumber) => {
        // When user clicks a recent tracking number, populate the input and trigger submission
        if (trackingInputRef.current) {
            trackingInputRef.current.setTrackingValue(trackingNumber);
        }
    };

    const handleModalClose = () => {
        setModalOpen(false);
        // Don't clear tracking data - keep it so user can reopen modal
        // Only clear when starting a new tracking scan
        // Return focus to the scan field so the next box can be scanned hands-free.
        setTimeout(() => trackingInputRef.current?.focus(), 0);
    };

    const handleNewScan = () => {
        // Clear all data when user wants to scan a new tracking number
        setTrackingData(null);
        setTrackingId(null);
        setSelectedRowData(null);
        setDispositionData(null);
        setGeneratedCases([]);
    };

    const handleCaseCreated = (caseData) => {
        // Add new case to the list
        setGeneratedCases(prev => [...prev, caseData]);
    };

    const handleReceiveUnitsClick = async (row) => {
        setSelectedRowData(row);
        setModalOpen(true);
        setLoading(true); // Start loading

        try {
            // ApiClient (axios) attaches the JWT and handles 401 refresh/redirect centrally.
            await ApiClient.post('/api/getproductreturnconditiondata', { row });
            // Lazy load dispositions for the selected row
            await loadDispositions(row);
        } catch (error) {
            console.error('Error fetching product return data:', error);
            toast.error('Failed to fetch product return data. Please try again.');
        } finally {
            setLoading(false); // Stop loading
        }
    };

    const loadDispositions = async (row) => {
        try {
            const response = await ApiClient.post('/api/getdispositionsforreturnedproducts', { removalShipment: row.removal_shipment });
            setDispositionData(response.data || []);
        } catch (error) {
            console.error('Error fetching dispositions data:', error);
            toast.error('Failed to fetch dispositions for returned products. Please try again.');
        }
    };

    const refreshTrackingData = async () => {
        // Re-fetch the tracking table after a save so received qty / status update
        // on screen without a manual page refresh.
        if (!trackingId) return;
        try {
            const response = await ApiClient.post('/api/getreturntrackingdata', { trackingId });
            if (Array.isArray(response.data)) {
                setTrackingData(response.data);
            }
        } catch (error) {
            console.error('Error refreshing tracking data:', error);
        }
    };

    return (
        <div className="space-y-8 px-4 sm:px-6 lg:px-8">
            <div className="border-b border-slate-200">
                <nav className="-mb-px flex gap-6" aria-label="Returns sections">
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setActiveTab(tab.key)}
                            className={`border-b-2 px-1 py-3 text-sm font-medium ${
                                activeTab === tab.key
                                    ? 'border-slate-900 text-slate-900'
                                    : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {activeTab === 'mfn' && <MerchantReturns />}

            {activeTab === 'fba' && (
              <>
            <UserTable
                onDataReceived={handleDataReceived}
                onTrackingSelect={handleTrackingSelect}
                trackingInputRef={trackingInputRef}
            />
            {trackingData && (
                <TrackingIdModalDataTable data={trackingData} onReceiveUnitsClick={handleReceiveUnitsClick} />
            )}
            {isModalOpen && (
                <ReceiveUnitsModal
                    open={isModalOpen}
                    onClose={handleModalClose}
                    data={selectedRowData}
                    dispositionData={dispositionData} // Pass dispositions data to the modal
                    trackingData={trackingData} // Pass tracking data to the modal
                    trackingId={trackingId} // Pass the tracking ID entered by user
                    generatedCases={generatedCases} // Pass generated cases
                    onCaseCreated={handleCaseCreated} // Callback for new cases
                    onSaved={refreshTrackingData} // Refresh table after a successful save
                />
            )}
            {loading && (
                <div className="text-center mt-4">
                    <p>Loading...</p>
                    <div className="animate-spin border-t-2 border-gray-500 rounded-full h-6 w-6 mx-auto"></div>
                </div>
            )}
              </>
            )}
        </div>
    );
}

export default Returns;
